import { Box, MenuList, Switch, TextField } from "@mui/material";
import { PartApplicability } from "../../../../../explorer/types/SpareParts/PartApplicability";
import { useTranslation } from "../../../../../explorer/hooks/useTranslation";
import { useEffect, useRef, useState } from "react";
import CommonDialog from "../../../CommonDialog/CommonDialog";
import { FormControlLabelStyled } from "../../../../pages/BasketPage/BasketPage.styles";
import { TypographyStyled } from "../../../CommonDialog/CommonDialog.style";
import { usePartFormatter } from "../../../../hook/usePartFormatter";
import {
    ApplicabilityListButton,
    ContentBox,
    ContentBoxRow,
    Li,
    MenuListCustom,
    MenuStyled,
    SearchApplicabilities,
    ToolbarStyled,
} from "./CodeAndDescriptionDialog.style";
import SkeletonList from "../../../../pages/SpareParts/HomePage/SkeletonList/SkeletonList";
import SearchNoResults from "../../../SearchNoResults/SearchNoResults";
import { EmojiFoodBeverage } from "@mui/icons-material";

type TCodeAndDescriptionDialog = {
    open: boolean;
    loading: boolean;
    applicabilities: PartApplicability[];
    code: string;
    rowId: string;
    customerSiteCode: string;
    navContext?: string;
    handleChangeNavContext: (
        navContext: string,
        valueSwitch: boolean
    ) => Promise<void>;
    handleCloseDialog: () => void;
};

const CodeAndDescriptionDialog = (props: TCodeAndDescriptionDialog) => {
    const {
        open,
        loading,
        applicabilities,
        code,
        rowId,
        customerSiteCode,
        navContext,
        handleChangeNavContext,
        handleCloseDialog,
    } = props;

    const { t } = useTranslation();
    const partFormatter = usePartFormatter();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [checked, setChecked] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string | undefined>("");
    const [machine, setMachine] = useState("");
    const [tableCode, setTableCode] = useState("");
    const [position, setPosition] = useState("");
    const [applicabilityList, setApplicabilityList] =
        useState<PartApplicability[]>(applicabilities);
    /**
     * Regex
     * @param {React.KeyboardEvent<HTMLDivElement>} event
     * @returns
     */
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const re = /[a-zA-Z0-9-\.\_]+/;
        if (!re.test(event.key)) {
            event.preventDefault();
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
        const re = /^[a-zA-Z0-9-\.\_]+$/;

        if (!re.test(event.clipboardData.getData("text"))) {
            event.preventDefault();
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setTimeout(() => {
            if (inputRef.current) inputRef.current.focus();
        }, 100);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleInputChange =
        (setter: React.Dispatch<React.SetStateAction<string>>) =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setter(event.target.value.trim());
        };

    const handleRemoveAssociation = () => {
        handleChangeNavContext("", checked);
    };

    const handleAddAssociation = () => {
        handleChangeNavContext(
            partFormatter.setNavContextByData(machine, tableCode, position),
            checked
        );
    };

    const onSearchHandle = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        event.stopPropagation();
        setSearchText(event.target.value);
    };

    const changeApplicability = (app: PartApplicability) => {
        if (app !== undefined) {
            setMachine(app.machine.serialNumber ?? "");
            setTableCode(app.bomItem.tableCode);
            setPosition(app.bomItem.position);
        }

        setAnchorEl(null);
        setSearchText(undefined);
    };

    useEffect(() => {
        partFormatter.getDataByNavContext(navContext ?? "", (m, t, p) => {
            setMachine(m);
            setTableCode(t);
            setPosition(p);
        });
    }, [navContext]);

    useEffect(() => {
        setApplicabilityList(applicabilities);
    }, [applicabilities]);

    useEffect(() => {
        setSearchText(undefined);
        setChecked(false);
        setAnchorEl(null);
        if (open && navContext)
            partFormatter.getDataByNavContext(navContext ?? "", (m, t, p) => {
                setMachine(m);
                setTableCode(t);
                setPosition(p);
            });
    }, [open]);

    const closeDialog = () => {
        handleCloseDialog();
        setMachine("");
        setTableCode("");
        setPosition("");
    };

    useEffect(() => {
        if (searchText !== undefined)
            setApplicabilityList(
                applicabilities.filter((x) => {
                    const navContext = `${x.machine.serialNumber} / ${x.machine.description} / ${x.bomItem.tableCode} / ${x.bomItem.position}`;

                    if (
                        navContext
                            .toUpperCase()
                            .includes(searchText.toUpperCase())
                    )
                        return x;
                })
            );
    }, [searchText]);

    return (
        <CommonDialog
            dialogTitleLabel={"common.basket.selectEquipmentTitle"}
            open={open}
            closeDialog={closeDialog}
            maxWidth={"md"}
            denyButton={handleRemoveAssociation}
            confirmButton={handleAddAssociation}
            confirmButtonText="common.associate"
            denyButtonText="common.disassociate"
        >
            <Box
                component="form"
                autoComplete="off"
                display={"flex"}
                flexDirection={"column"}
            >
                <Box>
                    <TypographyStyled>
                        {t("common.basket.filterByMachine")}
                    </TypographyStyled>
                </Box>
                <ContentBox>
                    <ContentBoxRow>
                        <ApplicabilityListButton
                            variant="contained"
                            onClick={handleMenuOpen}
                        >
                            {t("basket.selectApplicabilityList")}
                        </ApplicabilityListButton>
                        <MenuStyled
                            open={anchorEl !== null}
                            anchorEl={anchorEl}
                            onClose={handleMenuClose}
                        >
                            <ToolbarStyled>
                                <SearchApplicabilities
                                    type="text"
                                    label={t("basket.searchApplicabilities")}
                                    variant="standard"
                                    fullWidth
                                    value={searchText || ""}
                                    inputRef={(input) => {
                                        if (input) inputRef.current = input;
                                    }}
                                    onChange={onSearchHandle}
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                            </ToolbarStyled>
                            <MenuListCustom>
                                {loading ? (
                                    <SkeletonList
                                        mkey="CodeAndDescription"
                                        rows={6}
                                        cols={1}
                                    />
                                ) : applicabilityList.length > 0 ? (
                                    applicabilityList
                                        .slice(0, 100)
                                        .map(
                                            (
                                                app: PartApplicability,
                                                idx: number
                                            ) => (
                                                <Li
                                                    key={idx}
                                                    onClick={() =>
                                                        changeApplicability(app)
                                                    }
                                                >
                                                    <Box>
                                                        <div>
                                                            {`
                                                            ${app.machine.serialNumber}
                                                            / ${app.machine.description}
                                                            / ${app.bomItem.tableCode}
                                                            / ${app.bomItem.position}
                                                        `}
                                                        </div>
                                                    </Box>
                                                </Li>
                                            )
                                        )
                                ) : (
                                    <SearchNoResults />
                                )}
                            </MenuListCustom>
                        </MenuStyled>
                        <TextField
                            id="machine-textField"
                            label={t("common.basket.machineSerialNumber")}
                            variant="outlined"
                            value={machine}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                            onChange={handleInputChange(setMachine)}
                            sx={{ flexGrow: 2 }}
                        />
                        <TextField
                            id="tabelCode-textField"
                            label={t("common.basket.illustrationCode")}
                            variant="outlined"
                            value={tableCode}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                            onChange={handleInputChange(setTableCode)}
                        />
                        <TextField
                            id="postion-textField"
                            label={t("basket.position")}
                            variant="outlined"
                            value={position}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                            onChange={handleInputChange(setPosition)}
                        />
                    </ContentBoxRow>
                    <ContentBoxRow>
                        <FormControlLabelStyled
                            control={
                                <Switch
                                    onChange={(event) => {
                                        setChecked(event.target.checked);
                                    }}
                                />
                            }
                            label={t("common.basket.selectEquipment")}
                        />
                    </ContentBoxRow>
                </ContentBox>
            </Box>
        </CommonDialog>
    );
};

export default CodeAndDescriptionDialog;
